/**
 * Thin client over the durable packing-recording store that offloads the WRITE
 * path (begin/append/clear) to a Web Worker when one is available, and — in
 * worker mode only — drives the progressive upload of the proof-of-pack clip.
 *
 * Why: the proof-of-pack recorder emits a chunk every 250ms; mirroring each
 * into IndexedDB on the main thread was a primary cause of the Packing area
 * freezing during a recording. The worker (see `packing-recording-worker.ts`)
 * does those writes off-thread and PUTs the bytes to a GCS resumable session
 * as they accrue.
 *
 * Graceful degradation is the whole point: when `Worker` is unavailable — jsdom
 * unit tests, SSR, locked-down browsers, or a CSP that blocks the worker — this
 * falls straight back to calling the store on the main thread, so the recorder
 * keeps its full crash-recovery guarantee, exactly as before the worker
 * existed. In that fallback mode progressive upload is DISABLED
 * (`supportsUpload === false`, `finalizeUpload` resolves null), so the caller
 * uses the existing full-upload-at-stop path — today's behaviour unchanged.
 */
import {
  appendRecordingChunk,
  beginRecordingSession,
  clearRecordingSession,
  getRecordingSession,
  patchRecordingSession,
  type RecordingSessionMeta,
} from "@/lib/packing-recording-store";
import type {
  RecordingWorkerOutbound,
} from "@/lib/packing-recording-worker";

export type BeginMeta = Omit<RecordingSessionMeta, "updatedAt">;

/** Details needed to attach a progressively-uploaded clip without re-uploading. */
export interface CompletedUpload {
  objectPath: string;
  contentType?: string;
  fileName?: string;
  sizeBytes: number;
}

/**
 * Attach a stopped or recovered packing recording before a destructive job
 * handoff. The durable local copy is cleared only after the media row exists.
 */
export async function attachPackingRecordingBeforeHandoff({
  jobId,
  file,
  completedUpload,
  resumeUpload,
  attachProgressiveUpload,
  attachFile,
  clearPersisted,
}: {
  jobId: string;
  file: File | null;
  completedUpload?: CompletedUpload | null;
  resumeUpload?: () => Promise<CompletedUpload | null>;
  attachProgressiveUpload: (upload: CompletedUpload) => Promise<boolean>;
  attachFile: (file: File) => Promise<boolean>;
  clearPersisted: (jobId: string) => Promise<void>;
}): Promise<boolean> {
  let progressiveUpload = completedUpload ?? null;
  if (!progressiveUpload && resumeUpload) {
    progressiveUpload = await resumeUpload();
  }

  let attached = false;
  if (progressiveUpload?.objectPath && progressiveUpload.sizeBytes > 0) {
    attached = await attachProgressiveUpload(progressiveUpload);
  }
  if (!attached && file) {
    attached = await attachFile(file);
  }
  if (attached) {
    await clearPersisted(jobId);
  }
  return attached;
}

/** Hand-off payload to start progressive upload for a job. */
export interface BeginUploadInfo {
  jobId: string;
  sessionUrl: string;
  objectPath: string;
  contentType?: string;
  fileName?: string;
}

export interface UploadProgress {
  recordedBytes: number;
  uploadedBytes: number;
}

export interface RecordingMirror {
  /**
   * Whether this mirror can progressively upload (true only in worker mode).
   * When false, the caller should not bother minting a resumable session.
   */
  readonly supportsUpload: boolean;
  /** Begin (or restart) a durable session, clearing any stale chunks first. */
  begin(meta: BeginMeta): void;
  /**
   * Start progressively uploading the current session's bytes to the given GCS
   * resumable session. No-op in fallback mode. Safe to call after `begin`.
   */
  beginUpload(info: BeginUploadInfo): void;
  /** Abandon progressive upload for a job (e.g. the session mint failed). */
  abortUpload(jobId: string): void;
  /** Mirror one recorder chunk to the durable store (+ upload buffer). */
  append(jobId: string, blob: Blob): void;
  /**
   * Discard a durable session. Routed through the worker (when present) so it
   * is ordered AFTER any still-queued append messages for the same job —
   * clearing on the main thread could race ahead of a worker write and leave
   * orphan chunks behind.
   */
  clear(jobId: string): void;
  /**
   * Flush the final range and resolve with the completed object's details, or
   * null if progressive upload didn't complete (disabled, failed, or fallback
   * mode). MUST be called only after the recorder has stopped (so every chunk
   * has been posted), so the final byte count is correct.
   */
  finalizeUpload(jobId: string): Promise<CompletedUpload | null>;
  /** Subscribe to upload progress ticks. Returns an unsubscribe fn. */
  onProgress(cb: (jobId: string, p: UploadProgress) => void): () => void;
}

let worker: Worker | null = null;
let triedWorker = false;

function getWorker(): Worker | null {
  if (triedWorker) return worker;
  triedWorker = true;
  if (typeof Worker === "undefined") return null;
  try {
    worker = new Worker(
      new URL("./packing-recording-worker.ts", import.meta.url),
      { type: "module" },
    );
    // Best-effort: a worker error must never take down recording — the main
    // thread still reads the durable store directly for recovery.
    worker.onerror = () => {
      /* swallow; reads/recovery are unaffected */
    };
  } catch {
    worker = null;
  }
  return worker;
}

let mirror: RecordingMirror | null = null;

/** Singleton mirror — spawns the worker lazily on first use. */
export function getRecordingMirror(): RecordingMirror {
  if (mirror) return mirror;
  const w = getWorker();
  if (w) {
    const progressSubs = new Set<(jobId: string, p: UploadProgress) => void>();
    const pendingFinalize = new Map<
      string,
      (r: CompletedUpload | null) => void
    >();

    w.onmessage = (e: MessageEvent<RecordingWorkerOutbound>) => {
      const m = e.data;
      if (!m || typeof m !== "object") return;
      if (m.type === "progress") {
        progressSubs.forEach((cb) =>
          cb(m.jobId, {
            recordedBytes: m.recordedBytes,
            uploadedBytes: m.uploadedBytes,
          }),
        );
      } else if (m.type === "finalized") {
        const resolve = pendingFinalize.get(m.jobId);
        if (resolve) {
          pendingFinalize.delete(m.jobId);
          resolve(
            m.ok && m.objectPath
              ? {
                  objectPath: m.objectPath,
                  contentType: m.contentType,
                  fileName: m.fileName,
                  sizeBytes: m.sizeBytes ?? 0,
                }
              : null,
          );
        }
      }
    };

    mirror = {
      supportsUpload: true,
      begin(meta) {
        w.postMessage({ type: "begin", meta });
      },
      beginUpload(info) {
        w.postMessage({ type: "beginUpload", ...info });
      },
      abortUpload(jobId) {
        w.postMessage({ type: "abortUpload", jobId });
      },
      append(jobId, blob) {
        w.postMessage({ type: "chunk", jobId, blob });
      },
      clear(jobId) {
        w.postMessage({ type: "clear", jobId });
      },
      finalizeUpload(jobId) {
        return new Promise<CompletedUpload | null>((resolve) => {
          // If a previous finalize for this job is still pending, resolve it
          // null first so we never strand a promise.
          const prev = pendingFinalize.get(jobId);
          if (prev) prev(null);
          let settled = false;
          const settle = (r: CompletedUpload | null) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            resolve(r);
          };
          // Safety net: never hang the "Packing complete" flow on a stuck
          // worker — fall back to full upload after 30s.
          const timeout = setTimeout(() => {
            if (pendingFinalize.get(jobId) === settle) {
              pendingFinalize.delete(jobId);
            }
            settle(null);
          }, 30_000);
          pendingFinalize.set(jobId, settle);
          w.postMessage({ type: "finalize", jobId });
        });
      },
      onProgress(cb) {
        progressSubs.add(cb);
        return () => progressSubs.delete(cb);
      },
    };
  } else {
    mirror = {
      supportsUpload: false,
      begin(meta) {
        void beginRecordingSession(meta);
      },
      beginUpload() {
        /* no progressive upload without a worker */
      },
      abortUpload() {
        /* no-op */
      },
      append(jobId, blob) {
        void appendRecordingChunk(jobId, blob);
      },
      clear(jobId) {
        void clearRecordingSession(jobId);
      },
      finalizeUpload() {
        return Promise.resolve(null);
      },
      onProgress() {
        return () => {
          /* no-op */
        };
      },
    };
  }
  return mirror;
}

/**
 * Resume an INTERRUPTED progressive upload after a crash/reload and finalize it
 * WITHOUT re-uploading bytes already committed to GCS.
 *
 * The live worker's in-memory upload state is gone after a reload, so the normal
 * `finalizeUpload` path can't complete a session that was mid-flight. But the
 * resumable session URL, object path, and committed-byte watermark were
 * persisted to IndexedDB as the worker uploaded (see `RecordingSessionMeta`), so
 * here we:
 *   1. Read that persisted session state.
 *   2. Ask GCS for the AUTHORITATIVE committed offset (the persisted watermark
 *      can lag a range PUT whose meta-patch didn't land before the crash), so we
 *      never resend an already-stored range.
 *   3. PUT only the remaining suffix of the recovered clip and declare the
 *      total, closing the session.
 *
 * Best-effort: any missing state, unexpected status, or error resolves null so
 * the caller falls back to the existing full re-upload path. Runs on the main
 * thread — recovery is a one-off, not the hot recording path, so it doesn't need
 * the worker (and the worker's in-memory state is gone after a reload anyway).
 */
export async function resumeAndFinalizeUpload(
  jobId: string,
  recovered: Blob,
): Promise<CompletedUpload | null> {
  try {
    const meta = await getRecordingSession(jobId);
    if (!meta?.uploadSessionUrl || !meta.uploadObjectPath) return null;
    const sessionUrl = meta.uploadSessionUrl;
    const total = recovered.size;
    if (total === 0) return null;
    const result: CompletedUpload = {
      objectPath: meta.uploadObjectPath,
      contentType: meta.uploadContentType,
      fileName: meta.uploadFileName,
      sizeBytes: total,
    };
    // Already finalized before the crash — nothing left to upload.
    if (meta.uploadComplete) return result;

    // 1) Query the authoritative committed offset. A GCS resumable status query
    // is an empty PUT with `Content-Range: bytes */<total>`.
    const status = await fetch(sessionUrl, {
      method: "PUT",
      headers: { "Content-Range": `bytes */${total}` },
    });
    if (status.status === 200 || status.status === 201) {
      // Every byte was already committed before the crash; declare complete.
      await patchRecordingSession(jobId, {
        uploadCommittedBytes: total,
        uploadComplete: true,
      });
      return result;
    }
    if (status.status !== 308) return null; // unexpected — fall back

    // `Range: bytes=0-N` ⇒ N+1 bytes committed; an absent header ⇒ none.
    let committed = 0;
    const range = status.headers.get("Range");
    const m = range?.match(/bytes=0-(\d+)/);
    if (m) committed = parseInt(m[1], 10) + 1;
    if (committed > total) return null; // inconsistent — fall back

    // 2) Upload only the remaining suffix and declare the total to finalize. If
    // (defensively) everything is already committed, an empty `bytes */<total>`
    // PUT just closes the session without re-sending anything.
    const resp =
      committed >= total
        ? await fetch(sessionUrl, {
            method: "PUT",
            headers: { "Content-Range": `bytes */${total}` },
          })
        : await fetch(sessionUrl, {
            method: "PUT",
            headers: {
              "Content-Range": `bytes ${committed}-${total - 1}/${total}`,
            },
            body: recovered.slice(committed),
          });
    if (resp.status === 200 || resp.status === 201) {
      await patchRecordingSession(jobId, {
        uploadCommittedBytes: total,
        uploadComplete: true,
      });
      return result;
    }
    return null;
  } catch {
    return null;
  }
}
